import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  InputLabel,
  TextField,
  MenuItem,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../../../../components/CustomPagination";
import Modal from "../../../../../components/Modal";
import styles from "./style";
import color from "../../../../../constants/color";
import { formatDate } from "../../../../../utils/DateFormat";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc); // Extend dayjs with UTC plugin

const categoryOptions = [
  { _id: "Category 1", value: "Category 1" },
  { _id: "Category 2", value: "Category 2" },
  { _id: "Category 3", value: "Category 3" },
  { _id: "Category 4", value: "Category 4" },
];
const expenseAccountOptions = [
  { _id: "Expense Account 1", value: "Expense Account 1" },
  { _id: "Expense Account 2", value: "Expense Account 2" },
  { _id: "Expense Account 3", value: "Expense Account 3" },
  { _id: "Expense Account 4", value: "Expense Account 4" },
];
const reportingTagsOptions = [
  { _id: "Tag 1", value: "Tag 1" },
  { _id: "Tag 2", value: "Tag 2" },
];

const renderMenuItems = (options) =>
  options.map((item) => (
    <MenuItem key={item._id} value={item._id}>
      {item.value ? item.value : item.name}
    </MenuItem>
  ));

const UncategorizedTransactionList = ({
  data = [],
  page,
  setPage,
  total,
  handleUpdateUncategorizedTransactions,
  vendors,
  customers,
  refreshTransactions,
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [formData, setFormData] = useState({
    category: "",
    expenseAccount: "",
    vendor: "",
    customer: "",
    amount: "",
    reference: "",
    description: "",
    reportingTags: "",
    date: dayjs(),
  });

  const handleFormChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setFormData({
      category: "",
      expenseAccount: "",
      vendor: "",
      customer: "",
      amount: "",
      reference: "",
      description: "",
      reportingTags: "",
      date: dayjs(),
    });
  };

  const navigate = useNavigate();
  const getId = (row, idx) => row._id ?? row.id ?? idx;

  const handleToggle = useCallback(
    (id) => (e) => {
      e.stopPropagation();
      setSelected((prev) =>
        prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
      );
    },
    []
  );

  const handleToggleAll = (e) => {
    e.stopPropagation();
    const pageIds = data.map(getId);
    const allOnPage = pageIds.every((id) => selected.includes(id));

    setSelected((prev) =>
      allOnPage
        ? prev.filter((id) => !pageIds.includes(id))
        : [...prev, ...pageIds.filter((id) => !prev.includes(id))]
    );
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setModalOpen(true);

    const amount = transaction.deposit || transaction.withdrawal || "";
    setFormData({
      category: "",
      expenseAccount: "",
      vendor: "",
      customer: "",
      reference: transaction.reference || "",
      description: transaction.description || "",
      reportingTags: "",
      amount,
      date: transaction.date ? dayjs(transaction.date) : dayjs(),
    });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedTransaction(null);
    resetForm();
  };

  const updateUncategorizedTransaction = async () => {
    const finalData = { ...formData };

    const localDate = new Date(formData.date);
    const year = localDate.getFullYear();
    const month = localDate.getMonth();
    const date = localDate.getDate();
  
    const utcEndOfDay = new Date(Date.UTC(year, month, date, 23, 59, 57, 998));
    finalData.date = utcEndOfDay.toISOString();

    if (selectedTransaction.deposit) finalData.customer = formData.customer;
    if (selectedTransaction.withdrawal) finalData.vendor = formData.vendor;

    await handleUpdateUncategorizedTransactions(selectedTransaction._id, finalData);

    if (typeof refreshTransactions === "function") {
      refreshTransactions();
    }

    handleModalClose();
  };

  const allChecked =
    data.length > 0 && data.every((r, i) => selected.includes(getId(r, i)));
  const indeterminate = selected.length > 0 && !allChecked;
  const isSaveDisabled =
    !formData.category ||
    !formData.expenseAccount ||
    !(formData.vendor || formData.customer) ||
    !formData.amount ||
    !formData.reference ||
    !formData.description ||
    !formData.reportingTags;

  return (
    <Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.TableHead}>
                <Checkbox
                  sx={{ p: 0, m: 0 }}
                  checked={allChecked}
                  indeterminate={indeterminate}
                  onClick={handleToggleAll}
                />
              </TableCell>
              <TableCell sx={styles.TableHead}>Date</TableCell>
              <TableCell sx={styles.TableHead}>Description</TableCell>
              <TableCell sx={styles.TableHead}>Status</TableCell>
              <TableCell sx={styles.TableHead} align="right">
                Deposits
              </TableCell>
              <TableCell sx={styles.TableHead} align="right">
                Withdrawals
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="h6">No data</Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((item, idx) => {
                const rowId = getId(item, idx);
                return (
                  <TableRow
                    key={rowId}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleTransactionClick(item)}
                  >
                    <TableCell sx={styles.tableData}>
                      <Checkbox
                        sx={{ p: 0, m: 0 }}
                        checked={selected.includes(rowId)}
                        onClick={handleToggle(rowId)}
                      />
                    </TableCell>
                    <TableCell sx={styles.tableData}>{item.date
                      ? formatDate(item.date)
                      : ""}</TableCell>
                    <TableCell sx={styles.tableData}>
                      {item.description}
                    </TableCell>
                    <TableCell
                      sx={{ ...styles.tableData, color: color.lightText }}
                    >
                      {item.status}
                    </TableCell>
                    <TableCell sx={styles.tableData} align="right">
                      {item.deposit ? `$${item.deposit}` : ""}
                    </TableCell>
                    <TableCell sx={styles.tableData} align="right">
                      {item.withdrawal ? `$${item.withdrawal}` : ""}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <CustomPagination
          pageCount={total}
          totalItems={total}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
        />
      </TableContainer>

      <Modal
        open={modalOpen}
        setOpen={handleModalClose}
        title="Categorize Manually"
        btnText="Save"
        cancelText="Cancel"
        handleCheck={updateUncategorizedTransaction}
        disabled={isSaveDisabled}
      >
        <Box sx={styles.modalGrid}>
          <Box sx={styles.boxs2}>
            <InputLabel sx={styles.label}>Category</InputLabel>
            <TextField
              select
              name="category"
              value={formData.category}
              onChange={(e) => handleFormChange("category", e.target.value)}
              style={styles.boxs2}
            >
              <MenuItem value="" disabled>
                select
              </MenuItem>
              {renderMenuItems(categoryOptions)}
            </TextField>
          </Box>
        </Box>

        <Box sx={styles.modalGrid}>
          <Box sx={{ width: "50%", ml: "5px" }}>
            <InputLabel sx={styles.label}>Expense Account</InputLabel>
            <TextField
              name="expenseAccount"
              value={formData.expenseAccount}
              select
              required
              onChange={(e) =>
                handleFormChange("expenseAccount", e.target.value)
              }
              sx={styles.boxs2}
            >
              <MenuItem value="" disabled>
                select
              </MenuItem>
              {renderMenuItems(expenseAccountOptions)}
            </TextField>
          </Box>

          <Box sx={{ width: "50%", ml: "5px" }}>
            <InputLabel sx={styles.label}>Vendor</InputLabel>
            <TextField
              select
              name="vendor"
              value={formData.vendor}
              disabled={selectedTransaction?.deposit}
              onChange={(e) => handleFormChange("vendor", e.target.value)}
              sx={styles.boxs2}
            >
              <MenuItem value="" disabled>
                select
              </MenuItem>
              {renderMenuItems(vendors)}
            </TextField>
          </Box>
        </Box>

        <Box sx={styles.modalGrid}>
          <Box sx={styles.inputBoxs}>
            <Typography sx={styles.inputTexts}>Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box>
                <DatePicker
                  value={formData.date}
                  slotProps={{ field: { readOnly: true } }}
                  sx={styles.calenderInput}
                  onChange={(newValue) => handleFormChange("date", newValue)}
                />
              </Box>
            </LocalizationProvider>
          </Box>

          <Box sx={{ width: "50%", ml: "5px" }}>
            <InputLabel sx={styles.label}>Amount</InputLabel>
            <TextField
              type="number"
              name="amount"
              value={formData.amount}
              InputProps={{ readOnly: true }}
              sx={styles.boxs2}
              disabled
            />
          </Box>
        </Box>

        <Box sx={styles.modalGrid}>
          <Box sx={{ width: "50%", ml: "5px" }}>
            <InputLabel sx={styles.label}>Reference</InputLabel>
            <TextField
              name="reference"
              value={formData.reference}
              type="text"
              required
              onChange={(e) => handleFormChange("reference", e.target.value)}
              sx={styles.boxs2}
            />
          </Box>

          <Box sx={{ width: "50%", ml: "5px" }}>
            <InputLabel sx={styles.label}>Customer</InputLabel>
            <TextField
              select
              name="customer"
              disabled={selectedTransaction?.withdrawal}
              value={formData.customer}
              onChange={(e) => handleFormChange("customer", e.target.value)}
              sx={styles.boxs2}
            >
              <MenuItem value="" disabled>
                select
              </MenuItem>
              {renderMenuItems(customers)}
            </TextField>
          </Box>
        </Box>

        <Box sx={styles.modalGrid}>
          <Box sx={styles.boxs2}>
            <InputLabel sx={styles.label}>Description</InputLabel>
            <TextField
              multiline
              rows={2}
              name="description"
              value={formData.description}
              onChange={(e) => handleFormChange("description", e.target.value)}
              style={styles.boxs2}
            />
          </Box>
        </Box>

        <Box sx={styles.modalGrid}>
          <Box sx={styles.boxs2}>
            <InputLabel sx={styles.label}>Reporting Tags</InputLabel>
            <TextField
              select
              name="reportingTags"
              value={formData.reportingTags}
              onChange={(e) =>
                handleFormChange("reportingTags", e.target.value)
              }
              style={styles.boxs2}
            >
              <MenuItem value="" disabled>
                select
              </MenuItem>
              {renderMenuItems(reportingTagsOptions)}
            </TextField>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default UncategorizedTransactionList;
